import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight, Loader2, CalendarDays, Users, Ticket, Sparkles, Tag as TagIcon, X,
} from "lucide-react";
import PhotoDrop from "@/components/PhotoDrop";
import AddressSearch from "@/components/AddressSearch";

const SKILL_OPTIONS = [
  ["all_levels", "All levels"],
  ["beginner", "Beginner"],
  ["intermediate", "Intermediate"],
  ["advanced", "Advanced"],
];

function toLocalInputValue(d) {
  const pad = (n) => `${n}`.padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return toLocalInputValue(d);
}

export default function MeetupCreate() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get("edit");
  const [meta, setMeta] = useState({ categories: [], skill_levels: [] });
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setHours(18, 0, 0, 0);
    return toLocalInputValue(d);
  }, []);
  const [f, setF] = useState({
    title: "", description: "", category: "Networking",
    starts_at_local: defaultStart, ends_at_local: "",
    location: "", lat: null, lng: null,
    max_attendees: "",
    is_paid: false, price: "", currency: "USD",
    skill_level: "all_levels",
    tags: [], tagDraft: "",
    image: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/meetups/categories").then(r => setMeta(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editId) return;
    api.get(`/meetups/${editId}`).then(r => {
      const m = r.data || {};
      setF(prev => ({
        ...prev,
        title: m.title || "",
        description: m.description || "",
        category: m.category || "Networking",
        starts_at_local: isoToLocalInput(m.starts_at) || prev.starts_at_local,
        ends_at_local: isoToLocalInput(m.ends_at) || "",
        location: m.location || "",
        lat: m.lat ?? null, lng: m.lng ?? null,
        max_attendees: m.max_attendees ?? "",
        is_paid: !!m.is_paid,
        price: m.price ?? "",
        currency: m.currency || "USD",
        skill_level: m.skill_level || "all_levels",
        tags: m.tags || [],
        image: m.image || "",
      }));
    }).catch(e => toast.error(formatErr(e)));
  }, [editId]);

  const addTag = () => {
    const v = (f.tagDraft || "").trim().replace(/^#/, "");
    if (!v) return;
    if (f.tags.some(t => t.toLowerCase() === v.toLowerCase())) {
      setF({ ...f, tagDraft: "" });
      return;
    }
    if (f.tags.length >= 12) return;
    setF({ ...f, tags: [...f.tags, v], tagDraft: "" });
  };
  const removeTag = (t) => setF({ ...f, tags: f.tags.filter(x => x !== t) });

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Give it a title");
    if (!f.starts_at_local) return toast.error("Pick a start date & time");
    if (f.is_paid && (!f.price || Number(f.price) <= 0)) return toast.error("Paid meetups need a price > 0");
    const payload = {
      title: f.title,
      description: f.description,
      category: f.category,
      starts_at: new Date(f.starts_at_local).toISOString(),
      ends_at: f.ends_at_local ? new Date(f.ends_at_local).toISOString() : null,
      location: f.location,
      lat: f.lat, lng: f.lng,
      max_attendees: f.max_attendees ? Number(f.max_attendees) : null,
      is_paid: f.is_paid,
      price: f.is_paid ? Number(f.price) : 0,
      currency: (f.currency || "USD").toUpperCase(),
      skill_level: f.skill_level,
      tags: f.tags,
      image: f.image,
    };
    setBusy(true);
    try {
      if (editId) {
        await api.patch(`/meetups/${editId}`, payload);
        toast.success("Meetup updated");
        nav(`/meetups/${editId}`);
      } else {
        const { data } = await api.post("/meetups", payload);
        toast.success("Meetup posted!");
        nav(`/meetups/${data.id}`);
      }
    } catch (e2) {
      toast.error(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="meetup-create-form">
      <div>
        <p className="overline">{editId ? "Edit meetup" : "New meetup"}</p>
        <h1 className="h-display text-4xl mt-1">What are you gathering people for?</h1>
        <p className="text-[#4A4A4A] mt-1">Real people, real places, real connection. Fill it in and we&apos;ll do the rest.</p>
      </div>

      <div className="card p-6 space-y-4">
        <div>
          <label className="text-sm font-medium">Title <span className="text-[#B84B4B]">*</span></label>
          <input className="input mt-1.5" value={f.title} onChange={(e)=>setF({...f, title:e.target.value})}
                 placeholder="e.g. Sunrise surf & coffee at Praia do Guincho" data-testid="meetup-title-input" maxLength={160}/>
        </div>

        <div>
          <label className="text-sm font-medium">Description</label>
          <textarea rows={5} className="input mt-1.5 resize-none" value={f.description}
                    onChange={(e)=>setF({...f, description:e.target.value})}
                    placeholder="What's the vibe? What should people bring? Who's it for?"
                    data-testid="meetup-desc-input"/>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Category</label>
            <select className="input mt-1.5" value={f.category} onChange={(e)=>setF({...f, category:e.target.value})}
                    data-testid="meetup-category-input">
              {(meta.categories?.length ? meta.categories : ["Networking"]).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Skill level</label>
            <select className="input mt-1.5" value={f.skill_level}
                    onChange={(e)=>setF({...f, skill_level:e.target.value})}
                    data-testid="meetup-skill-input">
              {SKILL_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium inline-flex items-center gap-1"><CalendarDays size={12}/> Starts <span className="text-[#B84B4B]">*</span></label>
            <input type="datetime-local" className="input mt-1.5" value={f.starts_at_local}
                   onChange={(e)=>setF({...f, starts_at_local:e.target.value})}
                   data-testid="meetup-starts-input"/>
          </div>
          <div>
            <label className="text-sm font-medium inline-flex items-center gap-1"><CalendarDays size={12}/> Ends (optional)</label>
            <input type="datetime-local" className="input mt-1.5" value={f.ends_at_local}
                   onChange={(e)=>setF({...f, ends_at_local:e.target.value})}
                   data-testid="meetup-ends-input"/>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Location</label>
          <p className="text-[11px] text-[#8A8A8A] mt-0.5 mb-1.5">A real address — attendees will see a live map on the meetup page.</p>
          <AddressSearch
            value={f.location}
            placeholder="Search a venue, park, or address…"
            testid="meetup-location"
            onChange={(a)=>setF({...f, location: a.address || "", lat: a.lat ?? null, lng: a.lng ?? null})}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium inline-flex items-center gap-1"><Users size={12}/> Max attendees (optional)</label>
            <input type="number" min="1" className="input mt-1.5" value={f.max_attendees}
                   onChange={(e)=>setF({...f, max_attendees:e.target.value})}
                   placeholder="Leave blank for unlimited"
                   data-testid="meetup-max-input"/>
          </div>
          <div>
            <label className="text-sm font-medium inline-flex items-center gap-1"><Ticket size={12}/> Price</label>
            <div className="mt-1.5 flex gap-1">
              <button type="button" onClick={()=>setF({...f, is_paid:false, price:""})}
                data-testid="meetup-free-btn"
                className={`flex-1 text-xs px-3 py-2 rounded-full ${!f.is_paid ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E3] text-[#4A4A4A] hover:bg-[#F2F2F0]"}`}>
                Free
              </button>
              <button type="button" onClick={()=>setF({...f, is_paid:true})}
                data-testid="meetup-paid-btn"
                className={`flex-1 text-xs px-3 py-2 rounded-full ${f.is_paid ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E3] text-[#4A4A4A] hover:bg-[#F2F2F0]"}`}>
                Paid
              </button>
            </div>
            {f.is_paid && (
              <div className="mt-2 grid grid-cols-[1fr_auto] gap-2">
                <input type="number" step="0.01" min="0.01" className="input" value={f.price}
                       onChange={(e)=>setF({...f, price:e.target.value})}
                       placeholder="e.g. 10.00" data-testid="meetup-price-input"/>
                <select className="input" value={f.currency} onChange={(e)=>setF({...f, currency:e.target.value})}
                        data-testid="meetup-currency-input">
                  {["USD","EUR","GBP","BRL","CAD","AUD"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium inline-flex items-center gap-1"><TagIcon size={12}/> Tags</label>
          <p className="text-[11px] text-[#8A8A8A] mt-0.5 mb-1.5">Add up to 12 keywords — helps people find your meetup.</p>
          <div className="flex gap-2">
            <input className="input flex-1" value={f.tagDraft}
                   onChange={(e)=>setF({...f, tagDraft:e.target.value})}
                   onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                   placeholder="e.g. surf, portugal, sunrise" data-testid="meetup-tag-input"/>
            <button type="button" onClick={addTag} className="btn-secondary text-xs" data-testid="meetup-tag-add">Add</button>
          </div>
          {f.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="meetup-tag-list">
              {f.tags.map(t => (
                <span key={t} className="inline-flex items-center gap-1 badge-soft" data-testid={`meetup-tag-${t}`}>
                  #{t}
                  <button type="button" onClick={()=>removeTag(t)} aria-label="Remove tag"><X size={10}/></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="text-sm font-medium">Cover image (optional)</label>
          <div className="mt-1.5">
            <PhotoDrop value={f.image} onChange={(v)=>setF({...f, image:v})} aspect="cover" label="Drop a cover" testid="meetup-image"/>
          </div>
        </div>
      </div>

      <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="meetup-submit">
        {busy ? <Loader2 size={14} className="animate-spin"/> : <><Sparkles size={14}/> {editId ? "Save changes" : "Post meetup"} <ArrowRight size={14}/></>}
      </button>
    </form>
  );
}
